import { Command } from 'cmdk';
import { CalendarDays, Columns3, Library, TrendingUp, Gavel, FileText, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useUIStore } from '../stores/ui.js';

export function CommandPalette(): JSX.Element | null {
  const open = useUIStore((s) => s.commandPaletteOpen);
  const setOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const navigate = useNavigate();

  if (!open) {
    return null;
  }

  const goto = (path: string): void => {
    navigate(path);
    setOpen(false);
  };

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-ax-modal flex items-start justify-center pt-[15vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-ax-obsidian/80 backdrop-blur-sm" aria-hidden="true" />
      <div
        className="relative w-full max-w-2xl bg-ax-graphite border border-ax-line rounded-ax-lg shadow-ax-lg overflow-hidden animate-ax-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <Command label="Command palette" className="font-display">
          <div className="flex items-center gap-3 px-4 h-12 border-b border-ax-line">
            <Search size={16} className="text-ax-text-tertiary" />
            <Command.Input
              placeholder="O que você precisa fazer?"
              className="flex-1 bg-transparent text-ax-text-primary placeholder:text-ax-text-tertiary text-sm outline-none border-0"
              autoFocus
            />
            <span className="font-mono text-[10px] text-ax-text-tertiary">ESC</span>
          </div>
          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty className="px-3 py-6 text-center text-ax-text-tertiary text-sm">
              Nada por aqui ainda.
            </Command.Empty>

            <Command.Group heading="Navegar" className="text-xs">
              <Item icon={<CalendarDays size={14} />} label="Agenda crítica" hint="g a" onSelect={() => goto('/agenda')} />
              <Item icon={<Columns3 size={14} />} label="Kanban" hint="g k" onSelect={() => goto('/kanban')} />
              <Item icon={<Library size={14} />} label="Carteira" hint="g c" onSelect={() => goto('/carteira')} />
              <Item icon={<TrendingUp size={14} />} label="Painel de risco" hint="g r" onSelect={() => goto('/risco')} />
              <Item icon={<Gavel size={14} />} label="Processos" hint="g p" onSelect={() => goto('/processos')} />
            </Command.Group>

            <Command.Group heading="Ações">
              <Item icon={<Plus size={14} />} label="Cadastrar processo" onSelect={() => goto('/processos?new=1')} />
              <Item icon={<FileText size={14} />} label="Iniciar nova peça" onSelect={() => goto('/documentos?new=1')} />
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </div>
  );
}

function Item({
  icon,
  label,
  hint,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onSelect: () => void;
}): JSX.Element {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex items-center gap-2.5 px-3 py-2 rounded-ax-md text-ax-text-secondary aria-selected:bg-ax-night aria-selected:text-ax-text-primary cursor-pointer"
    >
      <span className="text-ax-text-tertiary">{icon}</span>
      <span className="flex-1 text-sm">{label}</span>
      {hint && <span className="font-mono text-[10px] text-ax-text-tertiary">{hint}</span>}
    </Command.Item>
  );
}
